import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Globe, LogOut, LayoutDashboard, Shield, Menu, X } from "lucide-react";
import { useState, useRef, useEffect } from "react";

const LANG_LABELS = { nl: "NL", en: "EN", de: "DE", fr: "FR", es: "ES", ar: "AR" };
const LANG_NAMES = { nl: "Nederlands", en: "English", de: "Deutsch", fr: "Fran\u00e7ais", es: "Espa\u00f1ol", ar: "\u0627\u0644\u0639\u0631\u0628\u064a\u0629" };

export default function Navbar() {
  const { user, logout } = useAuth();
  const { lang, setLang, toggleLang, t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef(null);

  useEffect(() => {
    const handleClick = (e) => { if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false); };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const isLanding = location.pathname === "/";

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 ${isLanding ? "glass-header" : "bg-white border-b border-border"}`} data-testid="main-navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
            <img src="https://customer-assets.emergentagent.com/job_ai-governance-ready/artifacts/1y03ujcj_CleverTion.png" alt="CleverTion" className="h-14 w-auto" />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-1">
            {isLanding && (
              <>
                <a href="#services" className="px-3 py-2 text-sm font-medium text-foreground/80 hover:text-foreground transition-colors" data-testid="nav-services-link">
                  {t("nav.services")}
                </a>
              </>
            )}
            {user && (
              <Link to="/dashboard" className="px-3 py-2 text-sm font-medium text-foreground/80 hover:text-foreground transition-colors flex items-center gap-1.5" data-testid="nav-dashboard-link">
                <LayoutDashboard size={14} />
                {t("nav.dashboard")}
              </Link>
            )}
            {user?.role === "admin" && (
              <Link to="/admin" className="px-3 py-2 text-sm font-medium text-foreground/80 hover:text-foreground transition-colors flex items-center gap-1.5" data-testid="nav-admin-link">
                <Shield size={14} />
                {t("nav.admin")}
              </Link>
            )}
          </nav>

          {/* Right side */}
          <div className="hidden md:flex items-center gap-2">
            <div className="relative" ref={langRef}>
              <button
                onClick={() => setLangOpen(!langOpen)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold tracking-wider uppercase border border-border rounded-sm hover:bg-secondary transition-colors"
                data-testid="language-toggle"
              >
                <Globe size={14} />
                {LANG_LABELS[lang]}
              </button>
              {langOpen && (
                <div className="absolute right-0 mt-1 bg-white border border-border shadow-lg z-50 min-w-[120px]" data-testid="language-dropdown">
                  {Object.entries(LANG_NAMES).map(([code, name]) => (
                    <button
                      key={code}
                      onClick={() => { setLang(code); setLangOpen(false); }}
                      className={`block w-full text-left px-3 py-2 text-xs hover:bg-secondary transition-colors ${lang === code ? "font-bold text-[#0A3FA3]" : ""}`}
                      data-testid={`lang-option-${code}`}
                    >
                      {name}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {user ? (
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground" data-testid="nav-user-name">{user.name || user.email}</span>
                <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="nav-logout-btn" className="text-xs">
                  <LogOut size={14} className="mr-1" />
                  {t("nav.logout")}
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Link to="/login">
                  <Button variant="ghost" size="sm" data-testid="nav-login-btn" className="text-xs">{t("nav.login")}</Button>
                </Link>
                <Link to="/register">
                  <Button size="sm" data-testid="nav-register-btn" className="text-xs bg-[#0A3FA3] hover:bg-[#082F7A]">{t("nav.register")}</Button>
                </Link>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <button className="md:hidden p-2" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-toggle">
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-border px-4 py-4 space-y-2" data-testid="mobile-menu">
          <div className="space-y-1 border-b border-border pb-2 mb-2">
            {Object.entries(LANG_NAMES).map(([code, name]) => (
              <button key={code} onClick={() => { setLang(code); setMobileOpen(false); }}
                className={`block w-full text-left px-3 py-2 text-sm ${lang === code ? "font-bold text-[#0A3FA3]" : ""}`}
                data-testid={`mobile-lang-${code}`}>{name}</button>
            ))}
          </div>
          {user && (
            <Link to="/dashboard" className="block px-3 py-2 text-sm" onClick={() => setMobileOpen(false)} data-testid="mobile-dashboard-link">
              {t("nav.dashboard")}
            </Link>
          )}
          {user?.role === "admin" && (
            <Link to="/admin" className="block px-3 py-2 text-sm" onClick={() => setMobileOpen(false)} data-testid="mobile-admin-link">
              {t("nav.admin")}
            </Link>
          )}
          {user ? (
            <button onClick={handleLogout} className="block px-3 py-2 text-sm text-red-600 w-full text-left" data-testid="mobile-logout-btn">{t("nav.logout")}</button>
          ) : (
            <>
              <Link to="/login" className="block px-3 py-2 text-sm" onClick={() => setMobileOpen(false)} data-testid="mobile-login-link">{t("nav.login")}</Link>
              <Link to="/register" className="block px-3 py-2 text-sm font-medium text-[#0A3FA3]" onClick={() => setMobileOpen(false)} data-testid="mobile-register-link">{t("nav.register")}</Link>
            </>
          )}
        </div>
      )}
    </header>
  );
}
